import { useMemo } from 'react'
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
  useMaterialReactTable,
} from 'material-react-table'
import { Box, IconButton, Tooltip } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import VisibilityIcon from '@mui/icons-material/Visibility'
import type { IBlogPostWithAuthor } from '../../../api/blog'
import { DateCell } from '../components/TableCells'

interface BlogPostsTableProps {
  data: IBlogPostWithAuthor[]
  isLoading: boolean
  pagination: MRT_PaginationState
  rowCount: number
  onPaginationChange: (updater: MRT_PaginationState | ((old: MRT_PaginationState) => MRT_PaginationState)) => void
  onView: (post: IBlogPostWithAuthor) => void
  onEdit: (post: IBlogPostWithAuthor) => void
  onDelete: (post: IBlogPostWithAuthor) => void
}

function ImageCell({ imgUrl }: { imgUrl: string | null }) {
  return imgUrl ? (
    <img
      src={imgUrl}
      alt="Cover"
      style={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 4 }}
    />
  ) : (
    <div style={{ width: 80, height: 50, backgroundColor: '#f0f0f0', borderRadius: 4 }} />
  )
}

function TitleCell({ title }: { title: string }) {
  return (
    <div style={{ 
      maxWidth: 250, 
      overflow: 'hidden', 
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      fontWeight: 500,
    }}>
      {title}
    </div>
  )
}

function SlugCell({ slug }: { slug: string }) {
  return (
    <div style={{ 
      maxWidth: 200, 
      overflow: 'hidden', 
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: '#666',
      fontFamily: 'monospace',
      fontSize: '0.85rem',
    }}>
      {slug}
    </div>
  )
}

function AuthorCell({ username, avatar }: { username: string; avatar: string | null }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {avatar ? (
        <img
          src={avatar}
          alt={username}
          style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }}
        />
      ) : (
        <div style={{ 
          width: 24, 
          height: 24, 
          borderRadius: '50%', 
          backgroundColor: '#e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.7rem',
          fontWeight: 600,
        }}>
          {username.charAt(0).toUpperCase()}
        </div>
      )}
      <span style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {username}
      </span>
    </div>
  )
}

function ContentPreviewCell({ content }: { content: string }) {
  // Strip HTML tags for preview
  const textContent = content.replace(/<[^>]*>/g, '').trim()
  return (
    <div style={{ 
      maxWidth: 300, 
      overflow: 'hidden', 
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      color: '#666',
      fontSize: '0.85rem',
    }}>
      {textContent || 'No content'}
    </div>
  )
}

export function BlogPostsTable({
  data,
  isLoading,
  pagination,
  rowCount,
  onPaginationChange,
  onView,
  onEdit,
  onDelete,
}: BlogPostsTableProps) {
  const columns = useMemo<MRT_ColumnDef<IBlogPostWithAuthor>[]>(
    () => [
      {
        accessorKey: 'coverImageUrl',
        header: 'Cover',
        size: 100,
        enableSorting: false,
        Cell: ({ cell }) => <ImageCell imgUrl={cell.getValue<string | null>()} />,
      },
      {
        accessorKey: 'title',
        header: 'Title',
        size: 250,
        Cell: ({ cell }) => <TitleCell title={cell.getValue<string>()} />,
      },
      {
        accessorKey: 'slug',
        header: 'Slug',
        size: 200,
        Cell: ({ cell }) => <SlugCell slug={cell.getValue<string>()} />,
      },
      {
        accessorKey: 'contentHtml',
        header: 'Content Preview',
        size: 300,
        enableSorting: false,
        Cell: ({ cell }) => <ContentPreviewCell content={cell.getValue<string>()} />,
      },
      {
        accessorKey: 'authorUsername',
        header: 'Author',
        size: 150,
        Cell: ({ row }) => (
          <AuthorCell 
            username={row.original.authorUsername} 
            avatar={row.original.authorAvatar} 
          />
        ),
      },
      {
        accessorKey: 'created',
        header: 'Created',
        size: 150,
        Cell: ({ cell }) => <DateCell date={cell.getValue<string>()} />,
      },
      {
        accessorKey: 'updated',
        header: 'Updated',
        size: 150,
        Cell: ({ cell }) => <DateCell date={cell.getValue<string>()} />,
      },
    ],
    [],
  )

  const table = useMaterialReactTable({
    columns,
    data,
    enableEditing: false,
    enableRowActions: true,
    positionActionsColumn: 'first',
    getRowId: (row) => row.id,
    manualPagination: true,
    rowCount,
    state: {
      isLoading,
      pagination,
      columnPinning: {
        left: ['mrt-row-actions'],
      },
    },
    onPaginationChange,
    enableColumnFilters: false,
    enableColumnActions: false,
    enableSorting: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableGlobalFilter: false,
    enableColumnPinning: true,
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '0.5rem' }}>
        <Tooltip title="View">
          <IconButton onClick={() => onView(row.original)} color="primary">
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton onClick={() => onEdit(row.original)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton onClick={() => onDelete(row.original)} color="error">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    muiTableContainerProps: {
      sx: {
        maxHeight: '100%',
        height: '100%',
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: 'none',
      },
    },
  })

  return <MaterialReactTable table={table} />
}
